import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store/root-reducer';

import {
  currentCompanyId,
  dataAgeInDays,
  groupedExperiences,
} from '../domain/profile-detail.rules';
import { type ExitSaveState, idleExitSave, idleNoteSave, type NoteSave } from './profile-detail.state';

const selectProfileDetail = (state: RootState) => state.profileDetail;

export const selectProfile = (state: RootState) => selectProfileDetail(state).profile;
/** Days since the data was last refreshed, measured when the profile record changes. */
export const selectDataAgeDays = createSelector(selectProfile, (profile) =>
  dataAgeInDays(profile, Date.now()),
);
export const selectLoadStatus = (state: RootState) => selectProfileDetail(state).loadStatus;
export const selectEnrich = (state: RootState) => selectProfileDetail(state).enrich;
export const selectDeactivate = (state: RootState) => selectProfileDetail(state).deactivate;
export const selectReactivate = (state: RootState) => selectProfileDetail(state).reactivate;

/** The admin "Add to list" dialog (#614). */
export const selectAddToList = (state: RootState) => selectProfileDetail(state).addToList;

/** Each type's main category, for the admin's type chip (#629). */
export const selectTypeCategories = (state: RootState) => selectProfileDetail(state).typeCategories;
export const selectTypesEdit = (state: RootState) => selectProfileDetail(state).typesEdit;
/** A client's "Types" dialog, for their licence (#723). */
export const selectLicenceTypesEdit = (state: RootState) =>
  selectProfileDetail(state).licenceTypesEdit;

/** The Notes tab (#676). */
export const selectNotes = (state: RootState) => selectProfileDetail(state).notes;

/**
 * The note change of `profileId`'s Notes tab (#743): its own entry, idle when
 * it has none. A change on another profile is never read here. Answers stored
 * objects only (no new one per call), for `useAppSelector`.
 */
export const selectNoteSave = (state: RootState, profileId: string): NoteSave =>
  selectProfileDetail(state).notes.saves[profileId] ?? idleNoteSave;

/** The admin "Verified by Invyo Expert" check of the identity band (#754). */
export const selectExpertValidation = (state: RootState) =>
  selectProfileDetail(state).expertValidation;

/**
 * The exit change of `profileId` (#755): its own entry, idle when it has none.
 * A change on another profile is never read here. Answers stored objects only,
 * for `useAppSelector`.
 */
export const selectExitSave = (state: RootState, profileId: string): ExitSaveState =>
  selectProfileDetail(state).exitSaves[profileId] ?? idleExitSave;

/** The admin contact-verification lifecycle (#556). */
export const selectContactStatus = (state: RootState) => selectProfileDetail(state).contactStatus;

// ── Network tab ──
export const selectCommonNetworkRows = (state: RootState) =>
  selectProfileDetail(state).commonNetwork.rows;
export const selectCommonNetworkLoading = (state: RootState) =>
  selectProfileDetail(state).commonNetwork.loading;
const selectCommonNetworkLoaded = (state: RootState) =>
  selectProfileDetail(state).commonNetwork.loaded;

/** Tab-badge count: `null` until the first load settles, then the real count. */
export const selectCommonNetworkCount = createSelector(
  selectCommonNetworkRows,
  selectCommonNetworkLoaded,
  (rows, loaded) => (loaded ? rows.length : null),
);

// ── Derived, memoised on the profile reference (pure domain rules) ──
export const selectGroupedExperiences = createSelector(selectProfile, groupedExperiences);
export const selectCurrentCompanyId = createSelector(selectProfile, currentCompanyId);
